import React, { useContext } from "react"
import PropTypes from "prop-types"
import { graphql } from "gatsby"
import styled, { ThemeContext } from "styled-components"
import { compose, layout } from "styled-system"
import { animation } from "../../lib/styled-system/system"

import Link from "../utils/Link"
import Image from "../utils/Image"
import { hexToRGBA } from "../../lib/color-helpers"

import Box from "../atoms/Box"
import Button from "../atoms/Button"
import Card from "../atoms/Card"
import ProportionalBox from "../atoms/ProportionalBox"
import { Label, Title } from "../atoms/Typography"
import RichText from "../atoms/RichText"
import Section from "../atoms/Section"
import TruncateText from "../utils/Truncate"
import { hasRichText } from "../../utils/utils"

const TextBox = styled(
  ({
    title,
    label,
    cardTitleVariant,
    cardTitleColor,
    descriptionRichText,
    className,
    textClassName,
    textAlign, // "left" | "center" | "right"
    button,
    lineClamp,
    ...rest
  }) => {
    return (
      <Box
        display="block"
        width="100%"
        textAlign={textAlign}
        className={className}
        style={{ zIndex: 100 }}
        {...rest}
      >
        {label && (
          <Label className={textClassName} mb={3}>
            {label}
          </Label>
        )}
        <Title
          transitionProperty="opacity"
          transitionTimingFunction="ease"
          transitionDuration="md"
          className={textClassName}
          variant={cardTitleVariant === "h1" ? "h1" : "h2"}
          as={cardTitleVariant === "h1" ? "h1" : "h2"}
          color={cardTitleColor}
          mb={3}
          textAlign={textAlign}
        >
          {title}
        </Title>
        {descriptionRichText?.content && (
          <TruncateText lineClamp={lineClamp} textAlign={textAlign}>
            <StyledRichText
              transitionProperty="opacity"
              transitionTimingFunction="ease"
              transitionDuration="md"
              className={textClassName}
              textAlign={textAlign}
              {...descriptionRichText}
            />
          </TruncateText>
        )}
        {button && (
          <Button
            aria-label="Button inside image card"
            className={textClassName}
            style={{ zIndex: 10, position: "relative" }}
            mt={4}
            mb={4}
            {...button}
          />
        )}
      </Box>
    )
  },
)`
  ${animation}
`

const StyledLink = styled(Link)`
  &:hover {
    & .image-container {
      // getting scale on image-container or image to avoid issue with image overflowing;
      ${props =>
        props.textBackground === "gradient" &&
        props.imageWidth === "full" &&
        props.overlayImage === null
          ? "transform: scale(1.05)"
          : null};

      & .image {
        ${props =>
          (props.textBackground === "solid" ||
            props.imageWidth === "default") &&
          props.overlayImage === null
            ? "transform: scale(1.05)"
            : null};
      }
    }
  }
`

const StyledCard = styled(Card)`
  & .shaved-text {
    opacity: ${props => (props.textVisibility !== "onHover" ? 1 : 0)};
  }
  &:hover {
    & .shaved-text {
      ${props => (props.textVisibility === "onHover" ? "opacity: 1" : null)};
    }
    & .shaved-text {
      opacity: ${props => (props.textVisibility === "onHover" ? 1 : 0.75)};
    }
  }
`

const StyledRichText = styled(RichText)`
  &.shaved-text {
    & .RichEditor-unstyled {
      margin-bottom: 0px; // removing unwanted margin from richText
    }
  }
  ${animation}
`

const StyledImage = styled(Image)`
  ${compose(layout)}
`

const StyledOverlayImage = styled(Image)`
  opacity: 0;
  transition: opacity 0.3s ease;
  will-change: opacity;
  &:hover {
    opacity: 1;
  }
`

const defaultCard = {
  borderRadius: "none",
  boxShadow: "md",
  bg: 0,
  theme: "light",
  useCard: false,
}

const BigImageCard = ({
  title,
  label,
  descriptionRichText,
  image,
  overlayImage,
  link,
  alignText,
  card,
  cardTitle,
  textBackground,
  textVisibility,
  backgroundImageDesktop,
  backgroundImageMobile,
  button,
  overlayOpacity,
  imageWidth,
  imageRatio = 1,
  lineClamp = 100,
  imageFieldId,
}) => {
  // setting a default value for card prop
  card =
    card && card.constructor === Object && Object.keys(card).length > 0
      ? card
      : defaultCard

  const currentTheme = useContext(ThemeContext)
  const bgColor = card
    ? currentTheme?.themes[card?.theme]?.colors?.background[card?.bg]
    : "#FFFFFF"
  const bgStartColor = hexToRGBA(bgColor, 100)
  const bgMidColor = hexToRGBA(bgColor, 90)
  const bgEndColor = hexToRGBA(bgColor, 0)
  const solidColorWithOpacity = hexToRGBA(bgColor, overlayOpacity * 100) //150)

  let showText = textVisibility !== "none" && card.useCard && bgColor
  let showGradient = textBackground === "gradient" && showText
  const hasDescription = hasRichText(descriptionRichText)

  {
    /*TODO:create a wrapper for card background image indiviual instance */
  }

  return (
    <StyledLink
      aria-label="Card link"
      optional
      {...link}
      textBackground={textBackground}
      imageWidth={imageWidth}
      overlayImage={overlayImage}
    >
      <StyledCard
        {...card}
        bg={null} // avoiding card to set bg color
        position="relative"
        textVisibility={textVisibility}
        textBackground={textBackground}
        height="100%"
        border="none"
      >
        <Section
          backgroundImageMobile={backgroundImageMobile}
          backgroundImageDesktop={backgroundImageDesktop}
          noStandardPadding={true}
          style={{
            background:
              backgroundImageMobile !== null || backgroundImageDesktop !== null
                ? solidColorWithOpacity
                : bgColor,
          }} // passing overlay color with opacity to section to get rid of image overflow issue
          overlayOpacity={overlayOpacity}
          height="100%"
          transitionProperty="opacity,background"
          transitionTimingFunction="ease"
          transitionDuration="md"
        >
          {image && (
            <ProportionalBox
              ratio={imageRatio}
              height={showText ? "auto" : "100%"}
              className="image-container"
              transitionProperty="height, transform"
              transitionTimingFunction="ease"
              transitionDuration="md"
              overflow="hidden"
            >
              <Box
                className="image"
                height="100%"
                transitionProperty="height, transform"
                transitionTimingFunction="ease"
                transitionDuration="md"
              >
                <StyledImage
                  {...image}
                  style={{
                    height: "100%",
                    width: "100%",
                  }}
                  imgStyle={{
                    borderBottomWidth: showText ? "1px" : "0px",
                    borderBottomStyle: "solid",
                    borderBottomColor: "transparent",
                    padding:
                      imageWidth === "full" || !card?.useCard ? "0px" : "24px",
                  }} // hiding gap between image and textBox
                  minHeight={!card?.useCard ? "358px" : "auto"}
                  imageFieldId={imageFieldId}
                />
              </Box>
              {overlayImage && (
                <Box
                  transitionProperty="height, transform"
                  transitionTimingFunction="ease"
                  transitionDuration="md"
                  height="100%"
                  className="image"
                >
                  <StyledOverlayImage
                    {...overlayImage}
                    style={{
                      height: "100%",
                      left: 0,
                      top: 0,
                      bottom: 0,
                      position: "absolute",
                      zIndex: 90,
                    }}
                    imgStyle={{
                      borderBottomWidth: showText ? "1px" : "0px",
                      borderBottomStyle: "solid",
                      borderBottomColor: "transparent",
                      padding:
                        imageWidth === "full" || !card?.useCard
                          ? "0px"
                          : "24px",
                    }}
                  />
                </Box>
              )}
              <Box
                transitionProperty="opacity"
                transitionTimingFunction="ease"
                transitionDuration="md"
                position="absolute"
                top={0}
                left={0}
                bottom={0}
                right={0}
                opacity={showGradient ? overlayOpacity : 0}
                background={`linear-gradient(to top, ${bgStartColor} 0%,${bgMidColor} 15%, ${bgEndColor}) 100%`}
                style={{ zIndex: 95, pointerEvents: "none" }}
              />
            </ProportionalBox>
          )}

          {showText && (title || hasDescription || button) && (
            <TextBox
              transitionProperty="opacity,background"
              transitionTimingFunction="ease"
              transitionDuration="md"
              textAlign={alignText}
              descriptionRichText={descriptionRichText}
              button={button}
              textClassName="shaved-text"
              className="text-box"
              title={title}
              label={label}
              {...cardTitle}
              height="100%"
              width="100%"
              left="0px"
              bottom="0px"
              mt={-4}
              py={6}
              px={5}
              bg={textBackground === "gradient" ? solidColorWithOpacity : null} // try to match gradient on textBox
              lineClamp={lineClamp}
            />
          )}
        </Section>
      </StyledCard>
    </StyledLink>
  )
}

export default BigImageCard

BigImageCard.strapiProps = {
  title: PropTypes.string,
  description: PropTypes.shape(RichText.strapiProps),
  image: PropTypes.object.isRequired,
}

BigImageCard.propTypes = {
  ...BigImageCard.strapiProps,
  cardBackground: PropTypes.oneOf(["none", "0", "1"]),
  cardShadow: PropTypes.bool,
  alignText: PropTypes.oneOf(["left", "center", "right"]),
  link: PropTypes.shape(Link.strapiProps),
}

export const query = graphql`
  fragment BigImageCard on STRAPI__COMPONENT_MOLECULES_BIG_IMAGE_CARD {
    title
    label
    backgroundImageMobile {
      ...ImageWithFullWidthLayout
    }
    backgroundImageDesktop {
      ...ImageWithFullWidthLayout
    }
    descriptionRichText {
      ...RichText
    }
    image {
      ...ImageWithConstrainedLayout
    }
    overlayImage {
      ...ImageWithConstrainedLayout
    }
    link {
      ...Link
    }
    button {
      ...Button
    }
  }
`
